---
id: libs-features-src-ab-testing-doc
title: AB Testing
owner: Luc Lemerez
status: Active
lastReviewed: 2026-01-27
visibility: internal
tags:
  - ab-testing
  - experimentation
  - feature-gating
---

## Product Overview

Provides a lightweight way to show or hide UI variants based on an assigned A/B
bucket. Product teams use it to compare alternative experiences in the app
while keeping a default control experience when a test is inactive or a user
has no assignment.

### Primary Workflow
Feature surfaces wrap variant UI in the AB Testing component, specifying the
experiment id and the bucket slots it should render for (such as control or
variant). The component renders children only when the current user is assigned
to the matching bucket.

### Business Logic
When no assignment is available, the test is inactive, or a guard condition is
false, the component renders only when the first slot is `control`. This keeps
experiments opt-in and prevents variant UI from rendering outside active tests.

### Outcomes
Variant-specific UI is gated to the intended bucket while control content
remains available as the default path.

### Out of Scope
Experiment assignment, analytics tracking, and bucket activation rules are
managed by the AB store and backend services rather than this component.

## Technical Overview

The AB Testing component is a MobX observer that reads `ABStore` from the root
store provider and decides whether to render its children. It compares the
requested `testId` and `slot` against the active test list and the user’s bucket
assignment before returning the UI.

### Entry Points
The feature entry point is `libs/features/src/ab-testing/ab-testing.tsx`, which
exports the `ABTesting` component and is re-exported from
`libs/features/src/ab-testing/index.ts` for `@rezi/features/ab-testing` imports.

### Key Modules
`ABTesting` consumes `ABStore.abAssign` and `ABStore.abIsActive` to determine the
current bucket and whether the test is active. The store lives in
`libs/stores/src/ab.ts` and is hydrated by the root store provider.

### Data and Services
AB assignment and activation data come from the shared AB store, which listens
for active tests and bucket assignments in Firebase and reads the assignment
cookie. This component itself does not fetch data; it only reads store state.

### Integrations and Side Effects
The component has no side effects beyond conditional rendering. It relies on
root store wiring in the app shell so `ABStore` is available at runtime.

### Constraints and Risks
If `ABStore` is unavailable or uninitialized, the component falls back to the
control slot behavior described above. Tests must ensure their control UI
remains valid in those scenarios.

## Changelog

- 2026-01-27: Created.
